import * as React from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DialogBodyProps } from '@fluentui/react-dialog'
import { Button, DialogActions, DialogBody, DialogContent, DialogTitle, DialogTrigger, makeStyles, MessageBar } from '@fluentui/react-components'
import FormContainer from '../../../../components/Form/FormContainer'
import { HammockDialogProps } from '../HammockDialog'
import { Destination } from '../../../../context/AxiosContext'
import { tokens } from '@fluentui/react-theme'
import { Message } from '@microsoft/microsoft-graph-types'
import useProcessEmail from '../../../../hooks/email/useProcessEmail'
import { IFileDto } from '../../../../config/fileConfig'
import { AxiosError } from 'axios'

const useStyles = makeStyles({
    deleteButton: {
        backgroundColor: tokens.colorPaletteRedForeground3,
        '&:hover, &:active': {
            backgroundColor: `${tokens.colorPaletteRedForeground1} !important`,
        },
        '&:hover:active': {
            backgroundColor: `${tokens.colorPaletteRedForeground2} !important`,
        },
    },
})

export type DeleteMessageDialogBodyProps = DialogBodyProps & {
    items: Message[]
    file?: IFileDto
    destination: Destination
    userEmail: string
    notificationEmail: string
    callback?: () => void
}

export default function DeleteMessageDialogBody(props: HammockDialogProps<DeleteMessageDialogBodyProps>) {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const dialogBodyProps = useMemo(() => props.dialogBodyProps?.() ?? ({} as DeleteMessageDialogBodyProps), [props])

    const [error, setError] = useState<string>()
    const title = translate('file.pivot.email.delete.title', { count: dialogBodyProps.items.length })
    const subTitle = translate('file.pivot.email.delete.subtitle', { count: dialogBodyProps.items.length })

    const { processSelection } = useProcessEmail()

    return (
        <DialogBody>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <FormContainer>
                    {error && (
                        <MessageBar intent={'error'} layout={'multiline'}>
                            {error}
                        </MessageBar>
                    )}
                    <div>{subTitle}</div>
                </FormContainer>
            </DialogContent>
            <DialogActions>
                <Button
                    appearance="primary"
                    className={styles.deleteButton}
                    onClick={() => {
                        return processSelection(
                            dialogBodyProps.userEmail,
                            dialogBodyProps.notificationEmail,
                            dialogBodyProps.items,
                            (references: string[]) => references.filter(reference => reference !== dialogBodyProps.file.reference),
                            () => {},
                            () => {
                                dialogBodyProps?.callback?.()
                            },
                        )
                            .then(() => props.onDismiss())
                            .catch((error: AxiosError) => {
                                setError(error.message)
                            })
                    }}
                >
                    {translate('file.pivot.document.delete.button')}
                </Button>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">{translate('action.cancel')}</Button>
                </DialogTrigger>
            </DialogActions>
        </DialogBody>
    )
}
